import React, { useState, useEffect } from 'react';
import { FileText, Download, Loader2, DollarSign } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import api from '../utils/api';

const FacturacionTab = () => {
  const [facturas, setFacturas] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    loadFacturas();
  }, []);

  const loadFacturas = async () => {
    try {
      setLoading(true);
      const response = await api.get('/facturacion/todas');
      setFacturas(response.data);
    } catch (error) {
      console.error('Error loading facturas:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las facturas',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateEstado = async (facturaId, nuevoEstado) => {
    try {
      await api.put(`/facturacion/${facturaId}/estado`, {
        estado: nuevoEstado
      });
      toast({
        title: '✅ Estado actualizado',
        description: 'El estado de la factura ha sido actualizado',
      });
      loadFacturas();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo actualizar el estado',
        variant: 'destructive',
      });
    }
  };

  const getEstadoBadge = (estado) => {
    const badges = {
      'Pendiente': 'bg-yellow-100 text-yellow-700',
      'Procesando': 'bg-blue-100 text-blue-700',
      'Completada': 'bg-green-100 text-green-700',
      'Error': 'bg-red-100 text-red-700'
    };
    return badges[estado] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#1e40af] mx-auto mb-4" />
        <p className="text-gray-600">Cargando facturas...</p>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-[#1e3a8a] flex items-center gap-2">
              <DollarSign className="h-6 w-6" />
              Gestión de Facturación
            </CardTitle>
            <p className="text-sm text-gray-600 mt-1">Total de solicitudes: {facturas.length}</p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {facturas.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <FileText className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <p>No hay solicitudes de factura</p>
          </div>
        ) : (
          <div className="space-y-4">
            {facturas.map((factura) => (
              <Card key={factura.id} className="border-2">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <h3 className="font-bold text-lg text-[#1e3a8a]">
                          Factura #{factura.id.substring(0, 8)}
                        </h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getEstadoBadge(factura.estado)}`}>
                          {factura.estado}
                        </span>
                      </div>
                      
                      <div className="grid md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <h4 className="font-semibold text-gray-700 mb-2">Datos del Cliente</h4>
                          <div className="space-y-1 text-sm">
                            <p><span className="text-gray-500">Nombre:</span> <span className="font-medium">{factura.user_nombre}</span></p>
                            <p><span className="text-gray-500">Email:</span> <span className="font-medium">{factura.user_email}</span></p>
                            <p><span className="text-gray-500">RFC:</span> <span className="font-medium">{factura.rfc}</span></p>
                            <p><span className="text-gray-500">Razón Social:</span> <span className="font-medium">{factura.razon_social}</span></p>
                          </div>
                        </div>
                        
                        <div>
                          <h4 className="font-semibold text-gray-700 mb-2">Datos Fiscales</h4>
                          <div className="space-y-1 text-sm">
                            <p><span className="text-gray-500">Régimen Fiscal:</span> <span className="font-medium">{factura.regimen_fiscal}</span></p>
                            <p><span className="text-gray-500">Uso CFDI:</span> <span className="font-medium">{factura.uso_cfdi}</span></p>
                            <p><span className="text-gray-500">CP:</span> <span className="font-medium">{factura.codigo_postal}</span></p>
                            <p><span className="text-gray-500">Monto:</span> <span className="font-bold text-green-600">${factura.monto_total} MXN</span></p>
                          </div>
                        </div>
                      </div>

                      <div className="border-t pt-3 text-xs text-gray-500">
                        <p>Orden ID: {factura.order_id}</p>
                        <p>Fecha de solicitud: {new Date(factura.fecha_solicitud).toLocaleString('es-MX')}</p>
                      </div>
                    </div>

                    <div className="ml-6 flex flex-col gap-2">
                      {factura.estado === 'Pendiente' && (
                        <>
                          <Button
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-700"
                            onClick={() => handleUpdateEstado(factura.id, 'Procesando')}
                          >
                            Procesar
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleUpdateEstado(factura.id, 'Error')}
                          >
                            Marcar Error
                          </Button>
                        </>
                      )}
                      
                      {factura.estado === 'Procesando' && (
                        <>
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleUpdateEstado(factura.id, 'Completada')}
                          >
                            <FileText className="h-4 w-4 mr-1" />
                            Completar
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUpdateEstado(factura.id, 'Pendiente')}
                          >
                            Volver a Pendiente
                          </Button>
                        </>
                      )}
                      
                      {factura.estado === 'Completada' && factura.factura_pdf_url && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.open(factura.factura_pdf_url, '_blank')}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Descargar PDF
                        </Button>
                      )}

                      {factura.estado === 'Error' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleUpdateEstado(factura.id, 'Pendiente')}
                        >
                          Reintentar
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FacturacionTab;
